import React, { useState, useEffect, useCallback } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtN = (n) => Number(n || 0).toLocaleString('en-IN');
const today = () => new Date().toISOString().split('T')[0];
const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };
const humanize = (s) => s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '-';

// ─── CSV Export ───────────────────────────────────────────────────────────────
const downloadCSV = (filename, cols, rows) => {
  const escape = (v) => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const header = cols.map(c => escape(c.label)).join(',');
  const lines = rows.map(row => cols.map(c => escape(c.csv ? c.csv(row) : (c.render ? c.render(row) : row[c.key]))).join(','));
  const csv = [header, ...lines].join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

function DownloadButton({ onClick, label = 'Download' }) {
  return (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, border: '1px solid #3b82f6', background: 'transparent', color: '#3b82f6', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
      {label}
    </button>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = '#3b82f6', icon, darkMode, T }) {
  return (
    <div style={{ background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: '18px 20px', boxShadow: darkMode ? '0 4px 16px rgba(0,0,0,0.25)' : '0 2px 8px rgba(15,31,69,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </div>
        <span style={{ fontSize: 11, color: T.text3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <p style={{ fontSize: 22, fontWeight: 800, color, margin: '0 0 4px' }}>{value}</p>
      {sub && <p style={{ fontSize: 11, color: T.text3, margin: 0 }}>{sub}</p>}
    </div>
  );
}

// ─── Section Wrapper ──────────────────────────────────────────────────────────
function Section({ title, subtitle, children, T, darkMode, actions }) {
  return (
    <div style={{ background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 16, overflow: 'hidden', boxShadow: darkMode ? '0 4px 24px rgba(0,0,0,0.25)' : '0 2px 12px rgba(15,31,69,0.06)' }}>
      <div style={{ padding: '16px 22px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: 0 }}>{title}</p>
          {subtitle && <p style={{ fontSize: 11, color: T.text3, margin: '3px 0 0' }}>{subtitle}</p>}
        </div>
        {actions}
      </div>
      <div style={{ padding: 22 }}>{children}</div>
    </div>
  );
}

// ─── Table ────────────────────────────────────────────────────────────────────
function ReportTable({ cols, rows, emptyMsg = 'No data found', T, darkMode }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
          <tr>
            {cols.map((c, i) => (
              <th key={i} style={{ padding: '10px 16px', textAlign: c.right ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={cols.length} style={{ padding: '48px 16px', textAlign: 'center', color: T.text3, fontSize: 13 }}>{emptyMsg}</td></tr>
          ) : rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${T.border}`, background: darkMode ? (i % 2 === 0 ? T.bg2 : T.bg3) : (i % 2 === 0 ? '#fff' : '#fafbff'), transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = darkMode ? '#1e3a5f' : '#f0f4ff'}
              onMouseLeave={e => e.currentTarget.style.background = darkMode ? (i % 2 === 0 ? T.bg2 : T.bg3) : (i % 2 === 0 ? '#fff' : '#fafbff')}>
              {cols.map((c, j) => (
                <td key={j} style={{ padding: '11px 16px', fontSize: 13, color: c.color ? c.color(row) : T.text2, textAlign: c.right ? 'right' : 'left', fontWeight: c.bold ? 600 : 400, whiteSpace: c.nowrap ? 'nowrap' : 'normal' }}>
                  {c.render ? c.render(row) : row[c.key] ?? '-'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Date Filter Bar ──────────────────────────────────────────────────────────
function DateFilter({ from, to, onFrom, onTo, onApply, loading, T, darkMode }) {
  const presets = [
    { label: 'This Month', fn: () => { onFrom(monthStart()); onTo(today()); } },
    { label: 'Last 3 Months', fn: () => { const d = new Date(); d.setMonth(d.getMonth() - 3); onFrom(d.toISOString().split('T')[0]); onTo(today()); } },
    { label: 'This Year', fn: () => { onFrom(`${new Date().getFullYear()}-01-01`); onTo(today()); } },
  ];
  const is = { border: `1px solid ${T.inputBorder}`, borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', background: T.inputBg, color: T.text };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '14px 22px', background: T.bg3, borderBottom: `1px solid ${T.border}` }}>
      {presets.map(p => (
        <button key={p.label} onClick={p.fn} style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.background = T.bg3}
          onMouseLeave={e => e.currentTarget.style.background = T.bg2}>
          {p.label}
        </button>
      ))}
      <div style={{ width: 1, height: 20, background: T.border, margin: '0 4px' }} />
      <input type="date" value={from} onChange={e => onFrom(e.target.value)} style={is} />
      <span style={{ fontSize: 12, color: T.text3 }}>to</span>
      <input type="date" value={to} onChange={e => onTo(e.target.value)} style={is} />
      <button onClick={onApply} disabled={loading} style={{ padding: '7px 18px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: loading ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
        {loading && <span style={{ width: 12, height: 12, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.6s linear infinite' }} />}
        Apply
      </button>
    </div>
  );
}

// ─── FINANCIAL REPORT ─────────────────────────────────────────────────────────
function FinancialReport({ token, darkMode, T }) {
  const [subTab, setSubTab] = useState('invoices');
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch_ = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    setLoading(true);
    try {
      const [inv, rec, cn, qt] = await Promise.all([
        fetch(`${BASE}/api/invoices?from=${from}&to=${to}`, { headers: h }).then(r => r.json()).catch(() => []),
        fetch(`${BASE}/api/invoices/receipts/all?from=${from}&to=${to}`, { headers: h }).then(r => r.json()).catch(() => []),
        fetch(`${BASE}/api/credit-notes?from=${from}&to=${to}`, { headers: h }).then(r => r.json()).catch(() => []),
        fetch(`${BASE}/api/invoices/quotations/all`, { headers: h }).then(r => r.json()).catch(() => []),
      ]);
      const invoicesAll = Array.isArray(inv) ? inv : [];
      const invoices = invoicesAll.filter(i => !i.invoice_date || (i.invoice_date >= from && i.invoice_date <= to));
      const receipts = Array.isArray(rec) ? rec : [];
      const credits = Array.isArray(cn) ? cn : [];
      const quotationsAll = Array.isArray(qt) ? qt : [];
      const quotations = quotationsAll.filter(q => !q.created_at || (q.created_at.slice(0, 10) >= from && q.created_at.slice(0, 10) <= to));
      const totalInvoiced = invoices.reduce((s, i) => s + Number(i.total_amount || 0), 0);
      const totalCollected = receipts.reduce((s, r) => s + Number(r.received_amount || 0), 0);
      const totalCredit = credits.reduce((s, c) => s + Number(c.total_amount || 0), 0);
      const totalQuoted = quotations.reduce((s, q) => s + Number(q.total_amount || 0), 0);
      const outstanding = totalInvoiced - totalCollected - totalCredit;
      setData({ invoices, receipts, credits, quotations, totalInvoiced, totalCollected, totalCredit, totalQuoted, outstanding });
    } catch (_) {}
    setLoading(false);
  }, [from, to, token]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const statusBadge = (status) => {
    const map = {
      paid: { color: '#16a34a', bg: darkMode ? 'rgba(22,163,74,0.12)' : '#f0fdf4' },
      unpaid: { color: '#dc2626', bg: darkMode ? 'rgba(220,38,38,0.12)' : '#fef2f2' },
      partially_paid: { color: '#d97706', bg: darkMode ? 'rgba(217,119,6,0.12)' : '#fffbeb' },
      overdue: { color: '#dc2626', bg: darkMode ? 'rgba(220,38,38,0.12)' : '#fef2f2' },
      sent: { color: '#3b82f6', bg: darkMode ? 'rgba(59,130,246,0.12)' : '#eff6ff' },
      cancelled: { color: T.text3, bg: T.bg3 },
      draft: { color: T.text3, bg: T.bg3 },
      accepted: { color: '#16a34a', bg: darkMode ? 'rgba(22,163,74,0.12)' : '#f0fdf4' },
      rejected: { color: '#dc2626', bg: darkMode ? 'rgba(220,38,38,0.12)' : '#fef2f2' },
      converted: { color: '#8b5cf6', bg: darkMode ? 'rgba(139,92,246,0.12)' : '#f5f3ff' },
      expired: { color: T.text3, bg: T.bg3 },
    };
    const s = map[status] || { color: T.text3, bg: T.bg3 };
    return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, color: s.color, background: s.bg }}>{humanize(status)}</span>;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <DateFilter from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={fetch_} loading={loading} T={T} darkMode={darkMode} />
      <div style={{ padding: '0 22px 0' }}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 20 }}>
          <StatCard label="Total Invoiced" value={fmt(data?.totalInvoiced)} color="#3b82f6" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>} />
          <StatCard label="Total Collected" value={fmt(data?.totalCollected)} color="#16a34a" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>} />
          <StatCard label="Outstanding" value={fmt(data?.outstanding)} color="#dc2626" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>} />
          <StatCard label="Credit Notes" value={fmt(data?.totalCredit)} color="#f59e0b" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>} />
          <StatCard label="Quoted" value={fmt(data?.totalQuoted)} color="#8b5cf6" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/></svg>} />
        </div>

        {/* Sub Tabs */}
        <div style={{ display:'flex', gap:4, background:T.bg3, border:`1px solid ${T.border}`, borderRadius:10, padding:4, width:'fit-content', marginBottom:20 }}>
          {[
            { id:'invoices', label:`Invoices (${data?.invoices?.length||0})` },
            { id:'receipts', label:`Receipts (${data?.receipts?.length||0})` },
            { id:'credits',  label:`Credit Notes (${data?.credits?.length||0})` },
            { id:'quotations', label:`Quotations (${data?.quotations?.length||0})` },
          ].map(st => (
            <button key={st.id} onClick={() => setSubTab(st.id)}
              style={{ padding:'6px 14px', borderRadius:7, border:'none', cursor:'pointer', fontSize:12, fontWeight:600, transition:'all 0.15s',
                background: subTab===st.id ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : 'transparent',
                color: subTab===st.id ? '#fff' : T.text3 }}>
              {st.label}
            </button>
          ))}
        </div>

        {subTab === 'invoices' && (
          <Section title="Invoice Summary" subtitle={`${data?.invoices?.length || 0} invoices in selected period`} T={T} darkMode={darkMode}
            actions={<DownloadButton onClick={() => downloadCSV(
              `Invoice_Report_${from}_to_${to}.csv`,
              [
                { label: 'Invoice No.', key: 'invoice_number' },
                { label: 'Invoice Date', csv: r => r.invoice_date ? new Date(r.invoice_date).toLocaleDateString('en-IN') : '-' },
                { label: 'Client Name', key: 'client_name' },
                { label: 'Client Group', csv: r => r.client_group || '-' },
                { label: 'Invoice Amount', csv: r => Number(r.total_amount || 0).toFixed(2) },
                { label: 'Amount Received', csv: r => Number(r.amount_paid || 0).toFixed(2) },
                { label: 'Unpaid (Balance)', csv: r => Number(r.balance_due || 0).toFixed(2) },
                { label: 'Status', csv: r => humanize(r.status) },
              ],
              data?.invoices || []
            )} />} >
            <ReportTable T={T} darkMode={darkMode}
              cols={[
                { label: 'Invoice No.', key: 'invoice_number', bold: true },
                { label: 'Client', key: 'client_name' },
                { label: 'Client Group', key: 'client_group', render: r => r.client_group || '-' },
                { label: 'Date', key: 'invoice_date', nowrap: true, render: r => r.invoice_date ? new Date(r.invoice_date).toLocaleDateString('en-IN') : '-' },
                { label: 'Amount', key: 'total_amount', right: true, render: r => fmt(r.total_amount) },
                { label: 'Received', key: 'amount_paid', right: true, render: r => fmt(r.amount_paid), color: () => '#16a34a' },
                { label: 'Unpaid', key: 'balance_due', right: true, render: r => fmt(r.balance_due), color: () => '#dc2626' },
                { label: 'Status', key: 'status', render: r => statusBadge(r.status) },
              ]}
              rows={data?.invoices || []} />
          </Section>
        )}

        {subTab === 'receipts' && (
          <Section title="Receipt Summary" subtitle={`${data?.receipts?.length || 0} receipts in selected period`} T={T} darkMode={darkMode}
            actions={<DownloadButton onClick={() => downloadCSV(
              `Receipt_Report_${from}_to_${to}.csv`,
              [
                { label: 'Receipt No.', key: 'receipt_number' },
                { label: 'Date', csv: r => r.payment_date ? new Date(r.payment_date).toLocaleDateString('en-IN') : '-' },
                { label: 'Client Name', key: 'client_name' },
                { label: 'Mode', csv: r => humanize(r.mode) },
                { label: 'Reference No.', csv: r => r.reference_number || '-' },
                { label: 'Amount Received', csv: r => Number(r.received_amount || 0).toFixed(2) },
              ],
              data?.receipts || []
            )} />} >
            <ReportTable T={T} darkMode={darkMode}
              cols={[
                { label: 'Receipt No.', key: 'receipt_number', bold: true },
                { label: 'Client', key: 'client_name' },
                { label: 'Date', key: 'payment_date', nowrap: true, render: r => r.payment_date ? new Date(r.payment_date).toLocaleDateString('en-IN') : '-' },
                { label: 'Mode', key: 'mode', render: r => humanize(r.mode) },
                { label: 'Amount', key: 'received_amount', right: true, render: r => fmt(r.received_amount), color: () => '#16a34a' },
              ]}
              rows={data?.receipts || []} />
          </Section>
        )}

        {subTab === 'credits' && (
          <Section title="Credit Notes" subtitle={`${data?.credits?.length || 0} credit notes in selected period`} T={T} darkMode={darkMode}
            actions={<DownloadButton onClick={() => downloadCSV(
              `Credit_Notes_Report_${from}_to_${to}.csv`,
              [
                { label: 'Credit Note No.', key: 'credit_note_number' },
                { label: 'Date', csv: r => r.date ? new Date(r.date).toLocaleDateString('en-IN') : '-' },
                { label: 'Client Name', key: 'client_name' },
                { label: 'Reason', key: 'reason' },
                { label: 'Amount', csv: r => Number(r.total_amount || 0).toFixed(2) },
              ],
              data?.credits || []
            )} />} >
            <ReportTable T={T} darkMode={darkMode}
              cols={[
                { label: 'Credit Note No.', key: 'credit_note_number', bold: true },
                { label: 'Client', key: 'client_name' },
                { label: 'Date', key: 'date', nowrap: true, render: r => r.date ? new Date(r.date).toLocaleDateString('en-IN') : '-' },
                { label: 'Reason', key: 'reason' },
                { label: 'Amount', key: 'total_amount', right: true, render: r => fmt(r.total_amount), color: () => '#dc2626' },
              ]}
              rows={data?.credits || []} />
          </Section>
        )}

        {subTab === 'quotations' && (
          <Section title="Quotation Summary" subtitle={`${data?.quotations?.length || 0} quotations in selected period`} T={T} darkMode={darkMode}
            actions={<DownloadButton onClick={() => downloadCSV(
              `Quotation_Report_${from}_to_${to}.csv`,
              [
                { label: 'Quote No.', key: 'quote_number' },
                { label: 'Date', csv: r => r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : '-' },
                { label: 'Client Name', csv: r => r.client_name || r.contact_name || '-' },
                { label: 'Title', key: 'title' },
                { label: 'Valid Till', csv: r => r.valid_till ? new Date(r.valid_till).toLocaleDateString('en-IN') : '-' },
                { label: 'Amount', csv: r => Number(r.total_amount || 0).toFixed(2) },
                { label: 'Status', csv: r => humanize(r.status) },
              ],
              data?.quotations || []
            )} />} >
            <ReportTable T={T} darkMode={darkMode}
              cols={[
                { label: 'Quote No.', key: 'quote_number', bold: true },
                { label: 'Client', key: 'client_name', render: r => r.client_name || r.contact_name || '-' },
                { label: 'Date', key: 'created_at', nowrap: true, render: r => r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : '-' },
                { label: 'Valid Till', key: 'valid_till', nowrap: true, render: r => r.valid_till ? new Date(r.valid_till).toLocaleDateString('en-IN') : '-' },
                { label: 'Amount', key: 'total_amount', right: true, render: r => fmt(r.total_amount) },
                { label: 'Status', key: 'status', render: r => statusBadge(r.status) },
              ]}
              rows={data?.quotations || []} />
          </Section>
        )}
      </div>
    </div>
  );
}

// ─── TASK REPORT ──────────────────────────────────────────────────────────────
function TaskReport({ token, darkMode, T }) {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetch_ = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/tasks?date_from=${from}&date_to=${to}`, { headers: h }).then(r => r.json()).catch(() => []);
      setTasks(Array.isArray(res) ? res : []);
    } catch (_) {}
    setLoading(false);
  }, [from, to, token]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const byStatus = tasks.reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {});
  const byBilling = tasks.reduce((acc, t) => { const k = t.billing_status || 'unbilled'; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  const byUser = tasks.reduce((acc, t) => { const k = t.assigned_names || t.assigned_name || 'Unassigned'; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  const overdue = tasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed');

  const statusColor = (s) => ({ completed: '#16a34a', pending: '#f59e0b', 'in-progress': '#3b82f6', 'on-hold': '#6b7280', overdue: '#dc2626' }[s] || '#94a3b8');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <DateFilter from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={fetch_} loading={loading} T={T} darkMode={darkMode} />
      <div style={{ padding: '0 22px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
          <StatCard label="Total Tasks" value={fmtN(tasks.length)} color="#3b82f6" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><rect x="9" y="2" width="6" height="4" rx="1"/><path d="M9 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-2"/></svg>} />
          <StatCard label="Completed" value={fmtN(byStatus.completed || 0)} color="#16a34a" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>} />
          <StatCard label="Overdue" value={fmtN(overdue.length)} color="#dc2626" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/></svg>} />
          <StatCard label="Unbilled" value={fmtN(byBilling.unbilled || 0)} color="#f59e0b" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>} />
        </div>

        {/* Status breakdown + Staff workload side by side */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Section title="Status Breakdown" T={T} darkMode={darkMode}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(byStatus).map(([status, count]) => (
                <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: statusColor(status), minWidth: 90, textTransform: 'capitalize' }}>{status}</span>
                  <div style={{ flex: 1, height: 8, borderRadius: 4, background: T.bg3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 4, background: statusColor(status), width: `${(count / tasks.length) * 100}%`, transition: 'width 0.4s ease' }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.text, minWidth: 30, textAlign: 'right' }}>{count}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Staff Workload" T={T} darkMode={darkMode}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(byUser).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, count]) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, color: T.text2, minWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                  <div style={{ flex: 1, height: 8, borderRadius: 4, background: T.bg3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 4, background: '#3b82f6', width: `${(count / Math.max(...Object.values(byUser))) * 100}%`, transition: 'width 0.4s ease' }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.text, minWidth: 24, textAlign: 'right' }}>{count}</span>
                </div>
              ))}
            </div>
          </Section>
        </div>

        {/* Overdue Tasks Table */}
        {overdue.length > 0 && (
          <Section title="⚠️ Overdue Tasks" subtitle={`${overdue.length} tasks past due date`} T={T} darkMode={darkMode}>
            <ReportTable T={T} darkMode={darkMode}
              cols={[
                { label: 'Task ID', key: 'task_number', bold: true, color: () => '#3b82f6' },
                { label: 'Service', key: 'service_name' },
                { label: 'Client', key: 'client_name' },
                { label: 'Assigned To', key: 'assigned_names', render: r => r.assigned_names || r.assigned_name || 'Unassigned' },
                { label: 'Due Date', key: 'due_date', nowrap: true, render: r => r.due_date ? new Date(r.due_date).toLocaleDateString('en-IN') : '-', color: () => '#dc2626' },
                { label: 'Status', key: 'status', render: r => <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, color: statusColor(r.status), background: darkMode ? 'rgba(220,38,38,0.12)' : '#fef2f2', textTransform: 'capitalize' }}>{r.status}</span> },
              ]}
              rows={overdue} />
          </Section>
        )}

        {/* All Tasks Table */}
        <Section title="All Tasks" subtitle={`${tasks.length} tasks in selected period`} T={T} darkMode={darkMode}>
          <ReportTable T={T} darkMode={darkMode}
            cols={[
              { label: 'Task ID', key: 'task_id', bold: true, color: () => '#3b82f6' },
              { label: 'Service', key: 'service_name' },
              { label: 'Client', key: 'client_name' },
              { label: 'Assigned To', key: 'assigned_to_name' },
              { label: 'Status', key: 'status', render: r => <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, color: statusColor(r.status), background: `${statusColor(r.status)}22`, textTransform: 'capitalize' }}>{r.status}</span> },
              { label: 'Billing', key: 'billing_status', render: r => <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, color: r.billing_status === 'billed' ? '#16a34a' : '#f59e0b', background: r.billing_status === 'billed' ? (darkMode ? 'rgba(22,163,74,0.12)' : '#f0fdf4') : (darkMode ? 'rgba(245,158,11,0.12)' : '#fffbeb') }}>{r.billing_status || 'unbilled'}</span> },
            ]}
            rows={tasks} />
        </Section>
      </div>
    </div>
  );
}

// ─── CLIENT REPORT ────────────────────────────────────────────────────────────
function ClientReport({ token, darkMode, T }) {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetch_ = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/clients?from=${from}&to=${to}`, { headers: h }).then(r => r.json()).catch(() => []);
      setClients(Array.isArray(res) ? res : []);
    } catch (_) {}
    setLoading(false);
  }, [from, to, token]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const byType = clients.reduce((acc, c) => { const k = c.client_type || 'Individual'; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  const byState = clients.reduce((acc, c) => { const k = c.state || 'Unknown'; acc[k] = (acc[k] || 0) + 1; return acc; }, {});

  const typeColors = ['#3b82f6', '#16a34a', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <DateFilter from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={fetch_} loading={loading} T={T} darkMode={darkMode} />
      <div style={{ padding: '0 22px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
          <StatCard label="Total Clients" value={fmtN(clients.length)} color="#3b82f6" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>} />
          <StatCard label="Active" value={fmtN(clients.filter(c => c.status === 'active').length)} color="#16a34a" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>} />
          <StatCard label="Inactive" value={fmtN(clients.filter(c => c.status !== 'active').length)} color="#94a3b8" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/></svg>} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Section title="By Client Type" T={T} darkMode={darkMode}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(byType).sort((a, b) => b[1] - a[1]).map(([type, count], i) => (
                <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, color: T.text2, minWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{humanize(type)}</span>
                  <div style={{ flex: 1, height: 8, borderRadius: 4, background: T.bg3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 4, background: typeColors[i % typeColors.length], width: `${(count / clients.length) * 100}%`, transition: 'width 0.4s ease' }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.text, minWidth: 30, textAlign: 'right' }}>{count}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="By State" T={T} darkMode={darkMode}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(byState).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([state, count], i) => (
                <div key={state} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, color: T.text2, minWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{state}</span>
                  <div style={{ flex: 1, height: 8, borderRadius: 4, background: T.bg3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 4, background: typeColors[i % typeColors.length], width: `${(count / clients.length) * 100}%`, transition: 'width 0.4s ease' }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.text, minWidth: 30, textAlign: 'right' }}>{count}</span>
                </div>
              ))}
            </div>
          </Section>
        </div>

        <Section title="Client List" subtitle={`${clients.length} clients`} T={T} darkMode={darkMode}>
          <ReportTable T={T} darkMode={darkMode}
            cols={[
              { label: 'File No.', key: 'file_number', bold: true, color: () => '#3b82f6' },
              { label: 'Client Name', key: 'client_name', bold: true },
              { label: 'Type', key: 'client_type', render: r => humanize(r.client_type) },
              { label: 'Group', key: 'group_name', render: r => r.group_name || '-' },
              { label: 'State', key: 'state' },
              { label: 'Status', key: 'status', render: r => <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, color: r.status === 'active' ? '#16a34a' : '#94a3b8', background: r.status === 'active' ? (darkMode ? 'rgba(22,163,74,0.12)' : '#f0fdf4') : T.bg3 }}>{r.status || 'active'}</span> },
            ]}
            rows={clients} />
        </Section>
      </div>
    </div>
  );
}

// ─── CRM REPORT ───────────────────────────────────────────────────────────────
function CRMReport({ token, darkMode, T }) {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetch_ = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/leads?from=${from}&to=${to}`, { headers: h }).then(r => r.json()).catch(() => []);
      setLeads(Array.isArray(res) ? res : []);
    } catch (_) {}
    setLoading(false);
  }, [from, to, token]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const byStage = leads.reduce((acc, l) => { acc[l.stage] = (acc[l.stage] || 0) + 1; return acc; }, {});
  const bySource = leads.reduce((acc, l) => { acc[l.lead_source || 'Unknown'] = (acc[l.lead_source || 'Unknown'] || 0) + 1; return acc; }, {});
  const won = leads.filter(l => l.stage === 'Won').length;
  const convRate = leads.length ? ((won / leads.length) * 100).toFixed(1) : 0;

  const stageColor = (s) => ({ 'New': '#3b82f6', 'Contacted': '#f59e0b', 'Qualified': '#8b5cf6', 'Proposal': '#ec4899', 'Won': '#16a34a', 'Lost': '#dc2626' }[s] || '#94a3b8');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <DateFilter from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={fetch_} loading={loading} T={T} darkMode={darkMode} />
      <div style={{ padding: '0 22px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
          <StatCard label="Total Leads" value={fmtN(leads.length)} color="#3b82f6" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>} />
          <StatCard label="Won" value={fmtN(won)} color="#16a34a" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>} />
          <StatCard label="Lost" value={fmtN(byStage['Lost'] || 0)} color="#dc2626" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>} />
          <StatCard label="Conversion Rate" value={`${convRate}%`} color="#8b5cf6" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Section title="Pipeline by Stage" T={T} darkMode={darkMode}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {['New', 'Contacted', 'Qualified', 'Proposal', 'Won', 'Lost'].map(stage => (
                <div key={stage} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: stageColor(stage), minWidth: 80 }}>{stage}</span>
                  <div style={{ flex: 1, height: 8, borderRadius: 4, background: T.bg3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 4, background: stageColor(stage), width: `${leads.length ? ((byStage[stage] || 0) / leads.length) * 100 : 0}%`, transition: 'width 0.4s ease' }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.text, minWidth: 24, textAlign: 'right' }}>{byStage[stage] || 0}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Lead Sources" T={T} darkMode={darkMode}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Object.entries(bySource).sort((a, b) => b[1] - a[1]).map(([source, count], i) => (
                <div key={source} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 12, color: T.text2, minWidth: 90, textTransform: 'capitalize' }}>{source}</span>
                  <div style={{ flex: 1, height: 8, borderRadius: 4, background: T.bg3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 4, background: ['#3b82f6', '#16a34a', '#f59e0b', '#8b5cf6', '#ec4899'][i % 5], width: `${leads.length ? (count / leads.length) * 100 : 0}%`, transition: 'width 0.4s ease' }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.text, minWidth: 24, textAlign: 'right' }}>{count}</span>
                </div>
              ))}
            </div>
          </Section>
        </div>

        <Section title="Lead List" subtitle={`${leads.length} leads in selected period`} T={T} darkMode={darkMode}>
          <ReportTable T={T} darkMode={darkMode}
            cols={[
              { label: 'Lead ID', key: 'lead_id', bold: true, color: () => '#3b82f6' },
              { label: 'Name', key: 'name', bold: true },
              { label: 'Company', key: 'company_name' },
              { label: 'Source', key: 'lead_source', render: r => <span style={{ textTransform: 'capitalize' }}>{r.lead_source || '-'}</span> },
              { label: 'Stage', key: 'stage', render: r => <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, color: stageColor(r.stage), background: `${stageColor(r.stage)}22` }}>{r.stage}</span> },
              { label: 'Created', key: 'created_at', nowrap: true, render: r => r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : '-' },
            ]}
            rows={leads} />
        </Section>
      </div>
    </div>
  );
}

// ─── COMPLIANCE REPORT ────────────────────────────────────────────────────────
function ComplianceReport({ token, darkMode, T }) {
  const [renewals, setRenewals] = useState([]);

  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    fetch(`${BASE}/api/renewals/all`, { headers: h }).then(r => r.json())
      .then(d => setRenewals(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [token]);

  const now = new Date();
  const due30 = renewals.filter(r => { const d = new Date(r.expiry_date); return d >= now && d <= new Date(now.getTime() + 30 * 86400000); });
  const overdue = renewals.filter(r => new Date(r.expiry_date) < now);
  const active = renewals.filter(r => new Date(r.expiry_date) >= now);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ padding: '14px 22px', background: T.bg3, borderBottom: `1px solid ${T.border}` }}>
        <p style={{ fontSize: 12, color: T.text3, margin: 0 }}>Showing all renewal data — use filters in sections below</p>
      </div>
      <div style={{ padding: '0 22px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
          <StatCard label="Due in 30 Days" value={fmtN(due30.length)} color="#f59e0b" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>} />
          <StatCard label="Overdue" value={fmtN(overdue.length)} color="#dc2626" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/></svg>} />
          <StatCard label="Active" value={fmtN(active.length)} color="#16a34a" darkMode={darkMode} T={T}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>} />
        </div>

        <Section title="🔴 Overdue Renewals" subtitle={`${overdue.length} renewals past expiry`} T={T} darkMode={darkMode}>
          <ReportTable T={T} darkMode={darkMode}
            cols={[
              { label: 'Client', key: 'client_name', bold: true },
              { label: 'Type', key: 'renewal_type' },
              { label: 'Expiry Date', key: 'expiry_date', nowrap: true, render: r => r.expiry_date ? new Date(r.expiry_date).toLocaleDateString('en-IN') : '-', color: () => '#dc2626' },
              { label: 'Days Overdue', key: 'expiry_date', right: true, render: r => { const d = Math.floor((now - new Date(r.expiry_date)) / 86400000); return <span style={{ color: '#dc2626', fontWeight: 700 }}>{d}d</span>; } },
            ]}
            rows={overdue} emptyMsg="No overdue renewals" />
        </Section>

        <Section title="🟡 Due in Next 30 Days" subtitle={`${due30.length} renewals coming up`} T={T} darkMode={darkMode}>
          <ReportTable T={T} darkMode={darkMode}
            cols={[
              { label: 'Client', key: 'client_name', bold: true },
              { label: 'Type', key: 'renewal_type' },
              { label: 'Expiry Date', key: 'expiry_date', nowrap: true, render: r => r.expiry_date ? new Date(r.expiry_date).toLocaleDateString('en-IN') : '-', color: () => '#f59e0b' },
              { label: 'Days Left', key: 'expiry_date', right: true, render: r => { const d = Math.floor((new Date(r.expiry_date) - now) / 86400000); return <span style={{ color: '#f59e0b', fontWeight: 700 }}>{d}d</span>; } },
            ]}
            rows={due30} emptyMsg="No renewals due in 30 days" />
        </Section>
      </div>
    </div>
  );
}

// ─── MAIN REPORTS COMPONENT ───────────────────────────────────────────────────
export default function Reports({ darkMode = false, T: theme }) {
  const T = theme || { bg: '#f4f7ff', bg2: '#fff', bg3: '#f8faff', border: '#e8edf5', border2: '#e2e8f0', text: '#0f1f45', text2: '#4a5568', text3: '#94a3b8', cardBorder: '#f1f5f9', inputBg: '#fff', inputBorder: '#e2e8f0' };
  const token = localStorage.getItem('token');

  const TABS = [
    { id: 'financial', label: 'Financial', icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg> },
    { id: 'tasks',     label: 'Tasks',     icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="2" width="6" height="4" rx="1"/><path d="M9 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-2"/><polyline points="9 12 11 14 15 10"/></svg> },
    { id: 'clients',   label: 'Clients',   icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg> },
    { id: 'crm',       label: 'CRM',       icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> },
    { id: 'compliance',label: 'Renewals',  icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg> },
  ];

  const [activeTab, setActiveTab] = useState('financial');

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box', padding: 24, background: darkMode ? 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.07) 0%,transparent 60%),#0a0f1e' : 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.05) 0%,transparent 50%),#f4f7ff' }}>

      {/* Header */}
      
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 14, padding: 5, width: 'fit-content', marginBottom: 20, flexShrink: 0, boxShadow: darkMode ? '0 2px 12px rgba(0,0,0,0.3)' : '0 1px 4px rgba(15,31,69,0.06)' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
              background: activeTab === tab.id ? 'linear-gradient(135deg,#0f1f45,#1a3a6b)' : 'transparent',
              color: activeTab === tab.id ? '#fff' : T.text3,
              boxShadow: activeTab === tab.id ? '0 2px 8px rgba(15,31,69,0.25)' : 'none',
            }}>
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minHeight: 0, background: T.bg2, borderRadius: 16, border: `1px solid ${T.cardBorder}`, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: darkMode ? '0 4px 24px rgba(0,0,0,0.3)' : '0 2px 12px rgba(15,31,69,0.06)' }}>
        <div style={{ flex: 1, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {activeTab === 'financial'  && <FinancialReport  token={token} darkMode={darkMode} T={T} />}
          {activeTab === 'tasks'      && <TaskReport        token={token} darkMode={darkMode} T={T} />}
          {activeTab === 'clients'    && <ClientReport      token={token} darkMode={darkMode} T={T} />}
          {activeTab === 'crm'        && <CRMReport         token={token} darkMode={darkMode} T={T} />}
          {activeTab === 'compliance' && <ComplianceReport  token={token} darkMode={darkMode} T={T} />}
        </div>
      </div>
    </div>
  );
}